import { InputBase, InputAdornment } from "@mui/material";
import { GridCustomCellProps } from "@progress/kendo-react-grid";
import { useEffect, useState } from "react";
import "./custom-cells.css";
import { useToast } from "@/dentlab/src/context/ToastContext";
import { useTableCellNavigation } from "./use-table-cell-navigation.hook";
import { useBulkChange } from "./use-bulk-change.hook";
import { useTableNavigation } from "../table-navigation-context";

function isTechnician(field: string | undefined) {
	return field?.includes("techn");
}

function isFirstTechnician(field: string | undefined) {
	return field === "techn_perc";
}

function isDisabled(field: string | undefined) {
	return isFirstTechnician(field); // if it's profile 1, the percentage is calculated instead of set
}

export const PercentageCustomCell = (props: GridCustomCellProps) => {
	const { showToast } = useToast();
	const [percentage, setPercentage] = useState<number>(0);
	const { visibleTechnicianGroupFields } = useTableNavigation();

	const { handleChange } = useBulkChange({
		dataItem: props.dataItem,
		dataIndex: props.dataIndex,
		field: props.field,
		onChange: props.onChange,
	});

	const horizontalGroup = isTechnician(props.field) ? visibleTechnicianGroupFields : undefined;

	const { handleKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
		field: props.field,
		dataIndex: props.dataIndex,
		horizontalGroup: horizontalGroup,
		focusSelector: (targetField: string, targetRowIndex: number) => {
			if (targetField.includes('techn_perc')) {
				return `input[data-field="${targetField}"][data-row-index="${targetRowIndex}"]`;
			}
			return `[data-testid="technician-autocomplete-trigger"][data-field="${targetField}"][data-row-index="${targetRowIndex}"]`;
		},
	});

	useEffect(() => {
		let percentageValue = 0;
		if (isTechnician(props.field) && isFirstTechnician(props.field)) {
			percentageValue =
				100 -
				(props.dataItem.techn_perc_2 ?? 0) -
				(props.dataItem.techn_perc_3 ?? 0);
		} else {
			percentageValue = props.field ? props.dataItem[props.field] : 0;
		}
		setPercentage(percentageValue);
	}, [props.dataItem, props.field]);

	const handlePercentageChange = (value: number) => {
		setPercentage(value);
	};

	const handlePercentageSubmit = (event: unknown) => {
		if (percentage > 100 || percentage < 0) {
			showToast("Prozentwert muss zwischen 0 und 100 liegen", "error");
			setPercentage(props.field ? props.dataItem[props.field] || 0 : 0);
			return;
		}
		if (props.onChange && props.field) {
			const currentValue = props.dataItem[props.field];
			if (currentValue !== percentage) {
				props.onChange({
					dataItem: props.dataItem,
					field: props.field,
					value: percentage,
					syntheticEvent: event as React.ChangeEvent<
						HTMLTextAreaElement | HTMLInputElement
					>,
					dataIndex: props.dataIndex,
				});
			}
		}
	};

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
	) => {
		if (event.key === "Enter") {
			handlePercentageSubmit(event);
			(event.target as HTMLInputElement).blur();
			return;
		}

		if (event.key === "Backspace" && event.target instanceof HTMLInputElement && event.target.value === "") {
			event.preventDefault();
			handleChange(0, event.metaKey || event.ctrlKey, event.shiftKey, event.altKey);
			return;
		}

		if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
			const input = event.target as HTMLInputElement;
			if (event.key === "ArrowRight") {
				const isAtEnd = input.selectionStart === input.value.length;
				if (!isAtEnd) return;
			} else {
				const isAtStart = input.selectionStart === 0;
				if (!isAtStart) return;
			}
		}

		handleNavigationKeyDown(event);
	};

	return (
		<td className="custom-td" key={props.dataItem.id}>
			<InputBase
				value={percentage}
				onChange={(event) => {
					handlePercentageChange(Number(event.target.value));
				}}
				onBlur={(event) => {
					handlePercentageSubmit(event);
				}}
				onKeyDown={handleKeyDown}
				endAdornment={
					<InputAdornment position="end">%</InputAdornment>
				}
				aria-describedby="weight-helper-text"
				inputProps={{
					"aria-label": "weight",
					"data-field": props.field,
					"data-row-index": props.dataIndex,
				}}
				disabled={isDisabled(props.field)}
				fullWidth
				sx={{
					height: "22px",
					width: "70px",
					fontSize: "12px",
					paddingLeft: 1,
					"& input": {
						textOverflow: "ellipsis",
						padding: 0,
						textAlign: "right"
					},
					"&:hover": {
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
					"&.Mui-focused": {
						backgroundColor: "rgba(0, 0, 0, 0.04)",
					},
					"&.Mui-disabled": {
						color: "rgba(0, 0, 0, 0.38)"
					},
					...(percentage > 100 || percentage < 0) && {
						color: "#d32f2f"
					}
				}}
			/>
		</td>
	);
};
